import type { CaptureResult, PostHog, PostHogConfig } from 'posthog-js';

// Keep the replay URL/CSS contract aligned with Rails' upload-boundary policy:
// https://github.com/easyllama/easyllama/blob/main/app/views/shared/_posthog_authoring_replay.js.erb
const credentialWords = new Set([
  'auth',
  'authorization',
  'bearer',
  'code',
  'cookie',
  'credential',
  'csrf',
  'jwt',
  'key',
  'nonce',
  'password',
  'secret',
  'session',
  'sig',
  'signature',
  'state',
  'token'
]);
const compactCredentials = new Set([
  'accesstoken',
  'apikey',
  'clientsecret',
  'idtoken',
  'oauthstate',
  'privatekey',
  'refreshtoken',
  'xamzcredential',
  'xamzsecuritytoken',
  'xamzsignature',
  'xgoogcredential',
  'xgoogsignature',
  'xgoogtoken',
  'authoringcontext'
]);

function credentialField(name: string, attribute = false): boolean {
  let decoded: string;
  try {
    decoded = decodeURIComponent(name);
  } catch (_) {
    decoded = name;
  }
  const words = String(decoded)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .split(/[^a-z0-9]+/);
  const compact = words.join('');
  return (
    words.some(function (word) {
      return (
        credentialWords.has(word) &&
        (!attribute || !['code', 'key', 'state'].includes(word))
      );
    }) ||
    Array.from(compactCredentials).some(function (field) {
      return compact.endsWith(field);
    })
  );
}

function redactUrl(value: string, depth = 0, visualResource = false): string {
  try {
    const url = new URL(value, 'https://replay.invalid');
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return value;
    let keepFragment = false;
    try {
      keepFragment =
        visualResource &&
        /^#[a-z0-9_.:-]{1,256}$/i.test(decodeURIComponent(url.hash));
    } catch (_) {
      /* Omit malformed fragments. */
    }
    let changed = Boolean(
      url.username || url.password || (url.hash && !keepFragment)
    );
    url.username = '';
    url.password = '';
    if (!keepFragment) url.hash = '';
    Array.from(url.searchParams.keys()).forEach(function (name) {
      if (credentialField(name)) {
        url.searchParams.delete(name);
        changed = true;
      } else {
        const values = url.searchParams.getAll(name);
        const sanitized = values.map(function (item) {
          if (!/^(https?:\/\/|\/)/i.test(item)) return item;
          // Bound nested redirect parsing and omit deeper opaque destinations.
          return (depth || 0) >= 3 ? '' : redactUrl(item, (depth || 0) + 1);
        });
        if (
          sanitized.some(function (item, index) {
            return item !== values[index];
          })
        ) {
          url.searchParams.delete(name);
          sanitized.forEach(function (item) {
            url.searchParams.append(name, item);
          });
          changed = true;
        }
      }
    });
    if (!changed) return value;
    if (url.origin !== 'https://replay.invalid') return url.href;
    const resourcePath = visualResource ? value.split(/[?#]/)[0] : url.pathname;
    return resourcePath + url.search + url.hash;
  } catch (_) {
    return '';
  }
}

function redactText(value: string, visualResource = false): string {
  // Parse whole URL inputs before looking for URLs embedded in prose or CSS.
  // Credential values can contain parentheses, brackets, and URL fragments.
  // SDK source masking can leave a literal <masked> inside a query parameter.
  if (/^(https?:\/\/|\/)(?:<masked>|[^\s"'<>])+$/i.test(value))
    return redactUrl(value, 0, visualResource);
  return value.replace(/(?:https?:\/\/|\/)[^\s"'<>]+/gi, function (candidate) {
    const suffix = (candidate.match(/[),;]+$/) || [''])[0];
    return (
      redactUrl(
        candidate.slice(0, candidate.length - suffix.length),
        0,
        visualResource
      ) + suffix
    );
  });
}

function redactStyle(value: string): string {
  let cursor = 0;
  let index = 0;
  let result = '';
  function replaceResource(start: number, end: number): void {
    const resource = value.slice(start, end);
    const safe = redactUrl(resource, 0, true);
    if (safe !== resource) {
      result += value.slice(cursor, start) + safe;
      cursor = end;
    }
  }
  while (index < value.length) {
    // Quotes inside comments are not CSS strings and must never swallow rules.
    if (value.slice(index, index + 2) === '/*') {
      const commentEnd = value.indexOf('*/', index + 2);
      if (commentEnd < 0) break;
      index = commentEnd + 2;
      continue;
    }
    const isUrl =
      value.slice(index, index + 4).toLowerCase() === 'url(' &&
      (index === 0 || !/[a-z0-9_-]/i.test(value[index - 1]));
    if (isUrl) {
      const contentStart = index + 4;
      let end = contentStart;
      let quote: string | null = null;
      for (; end < value.length; end += 1) {
        const character = value[end];
        if (character === '\\') {
          end += 1;
          continue;
        }
        if (quote) {
          if (character === quote) quote = null;
        } else if (character === '"' || character === "'") quote = character;
        else if (character === ')') break;
      }
      if (end === value.length) break;
      const content = value.slice(contentStart, end);
      const trimmed = content.trim();
      const quoted =
        (trimmed[0] === '"' || trimmed[0] === "'") &&
        trimmed[0] === trimmed[trimmed.length - 1];
      const resource = quoted ? trimmed.slice(1, -1) : trimmed;
      const resourceStart =
        contentStart + content.indexOf(trimmed) + (quoted ? 1 : 0);
      replaceResource(resourceStart, resourceStart + resource.length);
      index = end + 1;
    } else if (value[index] === '"' || value[index] === "'") {
      const delimiter = value[index];
      let stringEnd = index + 1;
      for (; stringEnd < value.length; stringEnd += 1) {
        if (value[stringEnd] === '\\') {
          stringEnd += 1;
          continue;
        }
        if (value[stringEnd] === delimiter) break;
      }
      if (stringEnd === value.length) break;
      if (/^(https?:\/\/|\/)/i.test(value.slice(index + 1, stringEnd)))
        replaceResource(index + 1, stringEnd);
      index = stringEnd + 1;
    } else index += 1;
  }
  return result + value.slice(cursor);
}

function redactValue(
  value: unknown,
  attributes = false,
  style = false
): unknown {
  if (typeof value === 'string')
    return style ? redactStyle(value) : redactText(value);
  if (value instanceof Date) return value;
  if (!value || typeof value !== 'object') return value;
  if (Array.isArray(value))
    return value.map(function (item) {
      return redactValue(item, attributes, style);
    });
  const record = value as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  Object.keys(record).forEach(function (key) {
    const item = record[key];
    if (attributes && credentialField(key, true)) result[key] = '[redacted]';
    else if (
      typeof item === 'string' &&
      /^(href|src|poster|action|formaction|xlink:href)$/i.test(key)
    )
      result[key] = redactUrl(
        item,
        0,
        attributes && !/^(action|formaction)$/i.test(key)
      );
    else if (
      typeof item === 'string' &&
      key === 'fontSource' &&
      /^https?:/i.test(item)
    )
      result[key] = redactUrl(item, 0, true);
    else if (typeof item === 'string' && attributes && key === 'srcset')
      result[key] = redactText(item, true);
    else
      result[key] = redactValue(
        item,
        key === 'attributes',
        style ||
          /^(style|_cssText|cssText|rule|replace|replaceSync|set|fontSource)$/i.test(
            key
          ) ||
          (attributes &&
            /^(fill|stroke|clip-path|filter|mask|marker-start|marker-mid|marker-end)$/i.test(
              key
            )) ||
          (key === 'textContent' && record.isStyle === true) ||
          (key === 'childNodes' && record.tagName === 'style')
      );
  });
  return result;
}

export const sanitizeEditorAnalyticsEvent = (
  event: CaptureResult | null
): CaptureResult | null => {
  if (!event) return event;
  if (event.event === '$snapshot') {
    const snapshots = event.properties.$snapshot_data;
    if (
      !Array.isArray(snapshots) ||
      snapshots.some(
        snapshot => snapshot && typeof snapshot === 'object' && 'cv' in snapshot
      )
    )
      return null;
  }
  try {
    return {
      ...event,
      properties: redactValue(event.properties) as CaptureResult['properties'],
      ...(event.$set && {
        $set: redactValue(event.$set) as CaptureResult['$set']
      }),
      ...(event.$set_once && {
        $set_once: redactValue(event.$set_once) as CaptureResult['$set_once']
      })
    };
  } catch {
    // Optional telemetry must never interrupt the editor or send an unreadable payload.
    return null;
  }
};

// These attribution formats are read by SDK 1.408.2's flags requests, which
// bypass before_send. Keep this compatibility boundary covered by a native SDK
// transport test when updating posthog-js.
const attributionKeys = [
  '$initial_person_info',
  '$initial_referrer_info',
  '$initial_campaign_params',
  '$stored_person_properties'
];

export const initializeEditorAnalytics = (
  client: Pick<
    PostHog,
    'get_property' | 'register' | 'unregister' | 'set_config'
  >
): void => {
  try {
    // Init reads only the shared dashboard cookie, avoiding stale local account
    // state. Restore normal storage before the first pageview and flags request.
    client.set_config({ persistence: 'localStorage+cookie' });
    for (const key of attributionKeys) {
      const value: unknown = client.get_property(key);
      if (value === undefined) continue;
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        client.unregister(key);
      } else {
        client.register({ [key]: redactValue(value) });
      }
    }
  } catch {
    // The original init config leaves this unset, so this runtime circuit
    // breaker prevents flags uploads if attribution cannot be made safe.
    client.set_config({ advanced_disable_flags: true });
  }
};

export const editorAnalyticsConfig: Partial<PostHogConfig> = {
  api_host: 'https://us.posthog.com',
  persistence: 'cookie',
  mask_personal_data_properties: true,
  custom_personal_data_properties: ['token'],
  loaded: initializeEditorAnalytics,
  before_send: sanitizeEditorAnalyticsEvent,
  session_recording: { compress_events: false }
};
